/**
 * Activity Sync Service
 *
 * Synchronizes computed activities from various sources into the unified activities table.
 * This ensures all trackable work items (training, certifications, calibrations, etc.)
 * are tracked consistently and appear in the Activities page.
 *
 * Note: Non-conformities are NOT auto-synced as activities. They are recorded in reports
 * and users can manually create activities from the report comment modal if needed.
 */

import type { Report } from '../schemas';
import type { Company, Site } from '../schemas/company.schemas';
import { ActivitiesRepo } from '../repos/activitiesRepo';
import {
  NEXT_CALIBRATION_KEY,
  TRAINING_SCHEDULED_KEY,
} from '@/features/calendar/utils/calendarEntries/constants';
import { resolvePlannedVisitAt } from '@/utils/inspectionScheduling';

/**
 * Parse a loose date string into a Date object.
 * Supports YYYY, YYYY-MM, YYYY-MM-DD, DD/MM/YYYY formats.
 */
function parseLooseDate(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  // Try ISO format first (YYYY-MM-DD)
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const date = new Date(
      parseInt(isoMatch[1]!, 10),
      parseInt(isoMatch[2]!, 10) - 1,
      parseInt(isoMatch[3]!, 10)
    );
    if (!isNaN(date.getTime())) return date;
  }

  // Try DD/MM/YYYY format
  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slashMatch) {
    const year =
      slashMatch[3]!.length === 2
        ? 2000 + parseInt(slashMatch[3]!, 10)
        : parseInt(slashMatch[3]!, 10);
    const date = new Date(year, parseInt(slashMatch[2]!, 10) - 1, parseInt(slashMatch[1]!, 10));
    if (!isNaN(date.getTime())) return date;
  }

  // Try YYYY-MM format (use first of month)
  const monthMatch = trimmed.match(/^(\d{4})-(\d{2})$/);
  if (monthMatch) {
    const date = new Date(parseInt(monthMatch[1]!, 10), parseInt(monthMatch[2]!, 10) - 1, 1);
    if (!isNaN(date.getTime())) return date;
  }

  // Try year only
  const yearMatch = trimmed.match(/^(\d{4})$/);
  if (yearMatch) {
    const date = new Date(parseInt(yearMatch[1]!, 10), 0, 1);
    if (!isNaN(date.getTime())) return date;
  }

  return undefined;
}

function formatDateToIso(date: Date): string {
  return date.toISOString().split('T')[0]!;
}

function getReportString(report: Report, key: string): string | undefined {
  const value = report.data.values[key];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
}

/**
 * Sync training activity from a report.
 */
async function syncTrainingActivity(report: Report, company?: Company, site?: Site): Promise<void> {
  const trainingDateRaw = getReportString(report, TRAINING_SCHEDULED_KEY);
  const trainingDate = parseLooseDate(trainingDateRaw);
  const sourceId = `training-${report.id}`;

  if (!trainingDate) {
    // Remove activity if no training date
    await ActivitiesRepo.deleteBySource('report', sourceId);
    return;
  }

  const companyName = company?.name ?? report.meta.company;
  const title = `Aggiornamento formazione — ${companyName ?? report.meta.title}`;

  await ActivitiesRepo.upsertFromSource({
    companyId: report.companyId,
    siteId: report.siteId,
    reportId: report.id,
    reportTitle: report.meta.title,
    reportVisitDate: report.meta.visitDate,
    reportSiteName: site?.name ?? report.meta.siteName,
    title,
    type: 'internal',
    category: 'training',
    priority: 'medium',
    dueDate: formatDateToIso(trainingDate),
    sourceType: 'report',
    sourceId,
    description: 'Formazione programmata',
  });
}

/**
 * Sync calibration activity from a report.
 */
async function syncCalibrationActivity(
  report: Report,
  _company?: Company,
  site?: Site
): Promise<void> {
  const calibrationDateRaw = getReportString(report, NEXT_CALIBRATION_KEY);
  const calibrationDate = parseLooseDate(calibrationDateRaw);
  const sourceId = `calibration-${report.id}`;

  if (!calibrationDate) {
    // Remove activity if no calibration date
    await ActivitiesRepo.deleteBySource('report', sourceId);
    return;
  }

  const siteName = site?.name ?? report.meta.siteName;
  const title = `Taratura sensori — ${siteName ?? report.meta.title}`;

  await ActivitiesRepo.upsertFromSource({
    companyId: report.companyId,
    siteId: report.siteId,
    reportId: report.id,
    reportTitle: report.meta.title,
    reportVisitDate: report.meta.visitDate,
    reportSiteName: siteName,
    title,
    type: 'internal',
    category: 'calibration',
    priority: 'medium',
    dueDate: formatDateToIso(calibrationDate),
    sourceType: 'report',
    sourceId,
    description: `Taratura programmata: ${calibrationDateRaw}`,
  });
}

/**
 * Sync draft report activity.
 */
async function syncDraftReportActivity(
  report: Report,
  company?: Company,
  site?: Site
): Promise<void> {
  const sourceId = `draft-report-${report.id}`;

  if (report.status === 'finalized') {
    // Close the activity if report is finalized
    await ActivitiesRepo.closeBySource('report', sourceId);
    return;
  }

  const companyName = company?.name ?? report.meta.company;
  const siteName = site?.name ?? report.meta.siteName;
  const context = [companyName, siteName].filter(Boolean).join(' · ');
  const title = context ? `Completa bozza — ${context}` : `Completa bozza — ${report.meta.title}`;

  await ActivitiesRepo.upsertFromSource({
    companyId: report.companyId,
    siteId: report.siteId,
    reportId: report.id,
    reportTitle: report.meta.title,
    reportVisitDate: report.meta.visitDate,
    reportSiteName: siteName,
    title,
    type: 'internal',
    category: 'draft-report',
    priority: 'medium',
    dueDate: report.meta.visitDate || undefined,
    sourceType: 'report',
    sourceId,
    description: 'Report in bozza da completare',
  });
}

/**
 * Sync all activities from a report (training, calibration, draft).
 * Note: Non-conformities are NOT synced as activities - they're recorded in reports.
 */
export async function syncReportActivities(
  report: Report,
  company?: Company,
  site?: Site
): Promise<void> {
  await Promise.all([
    syncTrainingActivity(report, company, site),
    syncCalibrationActivity(report, company, site),
    syncDraftReportActivity(report, company, site),
  ]);
}

/**
 * Sync certification activities for licensed personnel.
 */
export async function syncCertificationActivities(company: Company): Promise<void> {
  const licensedPersonnel = company.personnel.filter(
    (person) => person.licensed && person.licenseExpiry && !person.deletedAt
  );

  for (const person of licensedPersonnel) {
    const sourceId = `certification-${person.id}`;
    const expiryDate = parseLooseDate(person.licenseExpiry);

    if (!expiryDate) {
      await ActivitiesRepo.deleteBySource('personnel', sourceId);
      continue;
    }

    const fullName = `${person.lastName}${person.firstName ? ` ${person.firstName}` : ''}`.trim();
    const title = `Rinnovo patentino — ${fullName}`;

    await ActivitiesRepo.upsertFromSource({
      companyId: company.id,
      title,
      type: 'external',
      category: 'certification',
      priority: 'medium',
      dueDate: formatDateToIso(expiryDate),
      sourceType: 'personnel',
      sourceId,
      personId: person.id,
      description: person.licenseLastUpdate
        ? `Ultima formazione: ${person.licenseLastUpdate}`
        : undefined,
    });
  }
}

/**
 * Sync schedule inspection activity for a site without a scheduled date.
 */
export async function syncScheduleInspectionActivity(site: Site, company: Company): Promise<void> {
  const sourceId = `schedule-inspection-${site.id}`;

  // Check if site has refrigeration units (can't schedule inspections without them)
  const hasUnits = (site.refrigerationUnitsCount ?? 0) > 0;
  if (!hasUnits) {
    await ActivitiesRepo.deleteBySource('site', sourceId);
    return;
  }

  // Check if there's already a scheduled inspection
  const plannedVisitAt = resolvePlannedVisitAt(site);
  if (plannedVisitAt) {
    // Close/delete the activity if inspection is scheduled
    await ActivitiesRepo.deleteBySource('site', sourceId);
    return;
  }

  const title = `Pianifica sopralluogo — ${site.name}`;

  await ActivitiesRepo.upsertFromSource({
    companyId: company.id,
    siteId: site.id,
    title,
    type: 'internal',
    category: 'schedule-inspection',
    priority: 'medium',
    sourceType: 'site',
    sourceId,
    description: `${company.name} · ${site.name}`,
  });
}

/**
 * Close schedule inspection activity when an inspection is scheduled.
 */
export async function closeScheduleInspectionActivity(siteId: string): Promise<void> {
  const sourceId = `schedule-inspection-${siteId}`;
  await ActivitiesRepo.deleteBySource('site', sourceId);
}

/**
 * Activity Sync Service singleton with all sync methods.
 */
export const ActivitySyncService = {
  syncReportActivities,
  syncCertificationActivities,
  syncScheduleInspectionActivity,
  closeScheduleInspectionActivity,
  syncTrainingActivity,
  syncCalibrationActivity,
  syncDraftReportActivity,
};

// Backward compatibility aliases
/** @deprecated Use syncReportActivities instead */
export const syncReportActions = syncReportActivities;
/** @deprecated Use syncCertificationActivities instead */
export const syncCertificationActions = syncCertificationActivities;
/** @deprecated Use syncScheduleInspectionActivity instead */
export const syncScheduleInspectionAction = syncScheduleInspectionActivity;
/** @deprecated Use closeScheduleInspectionActivity instead */
export const closeScheduleInspectionAction = closeScheduleInspectionActivity;

/** @deprecated Use ActivitySyncService instead */
export const ActionSyncService = {
  syncReportActions: syncReportActivities,
  syncCertificationActions: syncCertificationActivities,
  syncScheduleInspectionAction: syncScheduleInspectionActivity,
  closeScheduleInspectionAction: closeScheduleInspectionActivity,
  syncNonConformityActions: async () => {
    // NC actions are no longer auto-synced - they're recorded in reports
    console.warn('syncNonConformityActions is deprecated and no longer syncs activities');
  },
  syncTrainingAction: syncTrainingActivity,
  syncCalibrationAction: syncCalibrationActivity,
  syncDraftReportAction: syncDraftReportActivity,
};
